import { supabase } from "@/lib/supabase";
import { haversineKm } from "@/lib/geo";
import { geocodeCities } from "@/lib/cityGeocode";

const BASE_SELECT =
  "*, host:profiles!events_host_id_fkey(display_name, username, avatar_url, full_name), rsvps(count)";

// --- Storage ---

const COVER_BUCKET = "event-covers";

export async function uploadCover(file, userId) {
  if (!file || !userId) return null;
  const ext = (file.name.split(".").pop() || "jpg").toLowerCase().replace(/[^a-z0-9]/g, "");
  const path = `${userId}/${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext || "jpg"}`;
  const { error } = await supabase.storage.from(COVER_BUCKET).upload(path, file, {
    cacheControl: "3600",
    upsert: false,
    contentType: file.type || "image/jpeg",
  });
  if (error) throw error;
  const { data } = supabase.storage.from(COVER_BUCKET).getPublicUrl(path);
  return data.publicUrl;
}

export async function deleteCoverByUrl(url) {
  if (!url || !url.includes(`/${COVER_BUCKET}/`)) return;
  const path = url.split(`/${COVER_BUCKET}/`)[1];
  if (!path) return;
  await supabase.storage.from(COVER_BUCKET).remove([path]);
}

function shape(row) {
  const hostName = row.host?.display_name || row.host?.full_name || "anon";
  const hostUsername = row.host?.username || null;
  const hostAvatar = row.host?.avatar_url || null;
  const attendeesCount = Array.isArray(row.rsvps) && row.rsvps.length
    ? row.rsvps[0].count
    : 0;
  // strip nested before returning
  const { host, rsvps, ...rest } = row; // eslint-disable-line no-unused-vars
  return {
    ...rest,
    host_name: hostName,
    host_username: hostUsername,
    host_avatar_url: hostAvatar,
    attendees_count: attendeesCount,
  };
}

// --- Read ---

// Date-based visibility: an event is visible for the ENTIRE calendar day
// of its `date_time` (in the viewer's LOCAL timezone) and only disappears
// from discovery once midnight passes locally. Implementation: compute the
// start of "today" in local time, convert to UTC ISO, and use it as a
// `gte` cutoff on `date_time` (a `timestamptz` column). Hosts still see
// their own past events via `listHostedEvents` (which does NOT apply this
// filter), preserving profile history and stats.
function startOfTodayLocalIso() {
  const now = new Date();
  const startLocal = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
  return startLocal.toISOString();
}

export async function listEvents({
  category, city, q, limit, signal, includeArchived = false,
  // When true, includes events flagged `is_private=true`. Private events
  // should NOT appear on the public feed, map, or homepage strip — they're
  // direct-link-only — so this defaults to false. Set true for host-side
  // surfaces such as MyEventsPage and the host's own public profile.
  includePrivate = false,
  // Radius discovery: when `center` is supplied we annotate each event with
  // exact haversine distance and (when `radiusKm` is set) filter on it
  // client-side. `radiusKm = null` means "anywhere" — no radius cutoff.
  //
  // `centerCity` is an OPTIONAL city-name hint used to keep legacy events
  // that have no `latitude`/`longitude` visible to users in the same city
  // (we can't compute distance for them so we fall back to a city-name match).
  // Without this fallback, hundreds of pre-Phase-2 seeded events disappear
  // from the feed.
  center = null, radiusKm = null, centerCity = null,
} = {}) {
  let query = supabase
    .from("events")
    .select(BASE_SELECT)
    .eq("is_deleted", false)
    .eq("is_hidden", false)
    .order("date_time", { ascending: true });

  // Private events are direct-link-only and must never surface in
  // discovery contexts (feed, map, homepage). Hosts can still see their
  // own private events on MyEvents / their public profile by passing
  // `includePrivate: true`.
  if (!includePrivate) {
    query = query.eq("is_private", false);
  }

  if (!includeArchived) {
    // Visible all calendar day until local midnight passes.
    query = query.eq("is_archived", false).gte("date_time", startOfTodayLocalIso());
  }

  if (category && category !== "All") query = query.eq("category", category);
  if (city && city !== "All") query = query.eq("city", city);

  // NOTE: We intentionally do NOT apply a SQL bounding-box pre-filter on
  // latitude/longitude here. Doing so silently drops every event with NULL
  // coordinates (legacy / seeded events), making the feed appear empty even
  // when matching events exist in the user's city. The client-side refine
  // below handles the radius cutoff and uses `centerCity` as a fallback for
  // events without coordinates.

  if (q) {
    const like = `%${q}%`;
    query = query.or(
      `title.ilike.${like},description.ilike.${like},city.ilike.${like}`
    );
  }
  if (limit) query = query.limit(limit);
  if (signal) query = query.abortSignal(signal);

  const { data, error } = await query;
  if (error) throw error;

  let rows = (data || []).map((r) => shape(r));

  // Distance annotation + (optional) radius refine when we have a center.
  if (center && center.lat != null && center.lng != null) {
    const cityKey = (centerCity || "").trim().toLowerCase();

    // Pass 1 — annotate distance using each event's own coords when present.
    rows = rows.map((e) => {
      const km = haversineKm(center, { lat: e.latitude, lng: e.longitude });
      return { ...e, distance_km: km };
    });

    // Pass 2 — for legacy events with no coords, geocode their `city` once
    // (cached in sessionStorage) so they participate in radius filtering
    // instead of being dropped. e.g. an "Anand" user with a 50 km radius
    // should now see Vadodara events (~38 km away).
    const missingCities = Array.from(
      new Set(
        rows
          .filter((e) => e.distance_km == null && e.city)
          .map((e) => e.city.trim().toLowerCase())
      )
    );
    if (missingCities.length) {
      const coords = await geocodeCities(missingCities, center);
      rows = rows.map((e) => {
        if (e.distance_km != null) return e;
        const key = (e.city || "").trim().toLowerCase();
        const cc = coords.get(key);
        return cc ? { ...e, distance_km: haversineKm(center, cc) } : e;
      });
    }

    // Filter + sort.
    rows = rows
      .filter((e) => {
        if (!radiusKm) return true; // Anywhere mode — keep all.
        // City-name "same city" rescue. ALWAYS keep events whose city matches
        // the user's centre city, even if the computed haversine distance is
        // large — this guards against the centre being slightly off (e.g. a
        // Mapbox forward-geocode landing on the wrong same-named city in
        // another region). An "Anand" user must always see Anand events.
        const sameCity = !!(cityKey && e.city && e.city.trim().toLowerCase() === cityKey);
        if (sameCity) return true;
        if (e.distance_km != null) return e.distance_km <= radiusKm;
        return false;
      })
      .sort((a, b) => {
        const da = a.distance_km ?? Number.POSITIVE_INFINITY;
        const db = b.distance_km ?? Number.POSITIVE_INFINITY;
        return da - db;
      });
  }

  return rows;
}

export async function getEvent(id, currentUserId) {
  const { data, error } = await supabase
    .from("events")
    .select(BASE_SELECT)
    .eq("id", id)
    .maybeSingle();
  if (error) throw error;
  if (!data) return null;
  // Hide soft-deleted / hidden events from non-host viewers.
  if ((data.is_deleted || data.is_hidden) && data.host_id !== currentUserId) {
    return null;
  }
  const e = shape(data);

  if (currentUserId) {
    const { data: rsvp } = await supabase
      .from("rsvps")
      .select("id, status")
      .eq("event_id", id)
      .eq("user_id", currentUserId)
      .maybeSingle();
    e.my_rsvp = rsvp || null;
    e.joined = !!rsvp && rsvp.status === "approved";
    e.pending = !!rsvp && rsvp.status === "pending";
  } else {
    e.my_rsvp = null;
    e.joined = false;
    e.pending = false;
  }
  return e;
}

export async function listCities() {
  const { data, error } = await supabase
    .from("events")
    .select("city")
    .order("city", { ascending: true });
  if (error) throw error;
  const set = new Set((data || []).map((r) => r.city).filter(Boolean));
  return Array.from(set).sort();
}

// Events the current user is hosting.
export async function listHostedEvents(userId) {
  const { data, error } = await supabase
    .from("events")
    .select(BASE_SELECT)
    .eq("host_id", userId)
    .order("date_time", { ascending: true });
  if (error) throw error;
  return (data || []).map(shape);
}

// Events the current user has RSVPed to (any status).
export async function listJoinedEvents(userId) {
  const { data, error } = await supabase
    .from("rsvps")
    .select(`status, event:events!rsvps_event_id_fkey(${BASE_SELECT})`)
    .eq("user_id", userId)
    .order("created_at", { ascending: false });
  if (error) throw error;
  return (data || [])
    .filter((r) => r.event)
    .map((r) => ({ ...shape(r.event), my_status: r.status }));
}

// All RSVPs for one event (host management).
export async function listEventRsvps(eventId) {
  const { data, error } = await supabase
    .from("rsvps")
    .select(
      "id, status, created_at, user:profiles!rsvps_user_id_fkey(id, display_name, username, avatar_url, whatsapp, instagram)"
    )
    .eq("event_id", eventId)
    .order("created_at", { ascending: true });
  if (error) throw error;
  return (data || []).map((r) => ({
    id: r.id,
    status: r.status,
    created_at: r.created_at,
    user_id: r.user?.id,
    display_name: r.user?.display_name || "anon",
    username: r.user?.username || null,
    avatar_url: r.user?.avatar_url || null,
    whatsapp: r.user?.whatsapp || null,
    instagram: r.user?.instagram || null,
  }));
}

// --- Write ---

export async function createEvent(input, userId) {
  // Frontend safety net — also enforced visually by the date picker's `min`.
  if (input.date_time) {
    const dt = new Date(input.date_time);
    if (Number.isFinite(dt.getTime()) && dt.getTime() < Date.now()) {
      throw new Error("Event date/time can't be in the past.");
    }
  }
  const row = {
    title: input.title.trim(),
    category: input.category,
    date_time: input.date_time,
    city: input.city.trim(),
    description: input.description.trim(),
    capacity: input.capacity ?? null,
    is_private: !!input.is_private,
    requires_approval: !!input.requires_approval,
    additional_notes: input.additional_notes?.trim() || null,
    cover_url: input.cover_url || null,
    latitude: input.latitude ?? null,
    longitude: input.longitude ?? null,
    exact_address: input.exact_address?.trim() || null,
    rough_area: input.rough_area?.trim() || null,
    host_id: userId,
  };
  const { data, error } = await supabase
    .from("events")
    .insert(row)
    .select("id")
    .single();
  if (error) throw error;
  return data.id;
}

export async function updateEvent(id, patch) {
  if (patch.date_time) {
    const dt = new Date(patch.date_time);
    if (Number.isFinite(dt.getTime()) && dt.getTime() < Date.now()) {
      throw new Error("Event date/time can't be in the past.");
    }
  }
  const allowed = [
    "title",
    "category",
    "date_time",
    "city",
    "description",
    "capacity",
    "is_private",
    "requires_approval",
    "additional_notes",
    "cover_url",
    "latitude",
    "longitude",
    "exact_address",
    "rough_area",
  ];
  const row = {};
  for (const k of allowed) {
    if (patch[k] !== undefined) row[k] = patch[k];
  }
  const { error } = await supabase.from("events").update(row).eq("id", id);
  if (error) throw error;
  return true;
}

export async function deleteEvent(id) {
  // RSVPs do not have ON DELETE CASCADE in our schema — clear them first.
  await supabase.from("rsvps").delete().eq("event_id", id);
  const { error } = await supabase.from("events").delete().eq("id", id);
  if (error) throw error;
  return true;
}

export async function joinEvent(eventId, userId, requiresApproval) {
  const status = requiresApproval ? "pending" : "approved";
  const { error } = await supabase
    .from("rsvps")
    .insert({ event_id: eventId, user_id: userId, status });
  if (error && error.code !== "23505") throw error; // unique violation = already RSVPed
  return status;
}

export async function setRsvpStatus(rsvpId, status) {
  const { error } = await supabase
    .from("rsvps")
    .update({ status })
    .eq("id", rsvpId);
  if (error) throw error;
  return true;
}

export async function removeRsvp(rsvpId) {
  const { error } = await supabase.from("rsvps").delete().eq("id", rsvpId);
  if (error) throw error;
  return true;
}

// --- Profiles ---

// `select *` so we transparently pick up v8 columns (state, country,
// location_lat, location_lng) when the migration has been run, while still
// working on the legacy v7 schema without breaking.
const PROFILE_FIELDS = "*";

export async function getProfile(userId) {
  const { data, error } = await supabase
    .from("profiles")
    .select(PROFILE_FIELDS)
    .eq("id", userId)
    .maybeSingle();
  if (error) throw error;
  return data;
}

// v8 columns — written best-effort with a v7-safe retry. Mirrors the
// approach used by AuthContext.ensureProfile so older DBs don't 500.
const V8_LOCATION_KEYS = ["state", "country", "location_lat", "location_lng"];

export async function updateProfile(userId, patch) {
  const row = {};
  [
    "display_name", "username", "full_name", "birthday", "gender",
    "avatar_url", "bio", "city", "whatsapp", "instagram", "snapchat",
    ...V8_LOCATION_KEYS,
  ].forEach((k) => {
    if (patch[k] !== undefined) row[k] = patch[k];
  });
  if (row.username) row.username = String(row.username).toLowerCase();
  const { error } = await supabase.from("profiles").update(row).eq("id", userId);
  if (!error) return true;
  // v7 fallback: schema is missing the location columns.
  if (V8_LOCATION_KEYS.some((k) => new RegExp(`\\b${k}\\b`, "i").test(error.message || ""))) {
    const legacy = { ...row };
    V8_LOCATION_KEYS.forEach((k) => { delete legacy[k]; });
    const { error: e2 } = await supabase.from("profiles").update(legacy).eq("id", userId);
    if (e2) throw e2;
    return true;
  }
  throw error;
}

// Check whether a username is taken by someone other than `selfId`.
export async function isUsernameTaken(username, selfId) {
  const cleaned = String(username || "").toLowerCase();
  if (!cleaned) return false;
  let q = supabase.from("profiles").select("id").ilike("username", cleaned).limit(1);
  if (selfId) q = q.neq("id", selfId);
  const { data, error } = await q;
  if (error) throw error;
  return (data || []).length > 0;
}
